import TableContainer from "../../../_table-container";
import UtilsService from "../../../../services/UtilsService";
import { mapState, mapActions } from "vuex";
import { StatusTypes } from "../../../../constants/status_types";

/**
 * Filter the data for the given stateID or code
 * @param {object} filter The status filter
 * @param {array} statuses The data to filter by
 */
const filterData = (filter, statuses) => {
  if (!filter || !filter.filter) {
    return statuses;
  }

  // for all cows -> return all data
  if (filter.filter === "allCows") {
    return statuses;
  }

  // for lactating -> filter on Dry status
  if (filter.filter === "lactating") {
    return statuses.filter(d => d.status !== StatusTypes.Dry);
  }

  // for non-lactating -> filter on non Dry status
  if (filter.filter === "nonLactating") {
    return statuses.filter(d => d.status === StatusTypes.Dry);
  }

  // // Else filter by status
  return statuses.filter(status => status.status === filter.filter);
};

/**
 * Filters the data wheter it contains the given string
 * @param {string} searchText The raw string typed in the search box
 * @param {array} data The data to search the given string in
 */
const searchData = (searchText, data) => {
  if (!searchText || searchText === "") {
    return data;
  }
  const searchedData = [];
  data.forEach(d => {
    if (
      Object.values(d).some(
        element =>
          element &&
          element
            .toString()
            .toLowerCase()
            .includes(searchText.toLowerCase())
      )
    ) {
      searchedData.push(d);
    }
  });
  return searchedData;
};

export default {
  name: "StatesTable",
  props: { searchText: String, tableTitle: String, selectedFilter: Object },
  components: { TableContainer },
  data(){
    return{
      sortBy: {
        key: "eartag",
        direction: false
      },
      headers: [
        {
          sortByKey: "eartag",
          text: this.$t("cowGenerics.eartag")
        },
        {
          sortByKey: "lactationNumber",
          text: this.$t("cowDetails.lactationNumber")
        },
        {
          sortByKey: "barn",
          text: this.$t("buildingGenerics.building")
        },
        {
          sortByKey: "lastCalved",
          text: this.$t("cowGenerics.lastCalving")
        },
        {
          sortByKey: "lastInsemination",
          text: this.$t("cowGenerics.lastInsemination")
        },
        {
          sortByKey: "birthDate",
          text: this.$t("cowGenerics.birthDate")
        },
        {
          sortByKey: "status",
          text: this.$t("dashboardView.listHeader.status")
        },
        {
          sortByKey: "dim",
          text: this.$t("dashboardView.listHeader.daysInMilk")
        },
        {
          sortByKey: "lastSeen",
          text: this.$t("dashboardView.listHeader.lastSeen")
        },
        {
          sortByKey: "heat_index",
          text: this.$t("dashboardView.listHeader.heatIndex")
        },
        {
          sortByKey: "pendingMsg",
          text: this.$t("dashboardView.listHeader.pendingMsgs")
        },
      ],
    }
  },
  computed: {
    ...mapState({
      /**
       * The computed data for the table
       * @param {object} state The state object
       */
      tableData(state) {
        const filteredData = filterData(this.selectedFilter, state.states.statuses);
        const formattedData = this.formatData(filteredData, state.sensorLogs.latest);
        const searchedData = searchData(this.searchText, formattedData);
        const sortedData = this.sortData(searchedData);
        this.setCowList(sortedData);
        return sortedData;
      }
    })
  },
  methods: {
    ...mapActions("cows", {
      setCowList: "setCowList",
    }),
    setSortBy(sortBy) {
      this.sortBy.direction = !this.sortBy.direction;
      this.sortBy.key = sortBy;
    },
    /**
     * Format data from store to displaying
     * @param {array} data The raw data from store to format
     */
    formatData(statuses, sensor_logs) {
      return statuses.filter(status=>status.cow)
        .map(status => {
        const sensor_log = sensor_logs.find(({cow_id}) => cow_id == status.cow_id)
        const cow = status.cow;
        return {
          id: status.status_id,
          cow_id: status.cow_id,
          eartag: cow && cow.eartag,
          lactationNumber: cow && cow.lactation_number,
          barn: cow && cow.building.building_name,
          status: status.status,
          dim: cow && cow.last_calving && UtilsService.get_dim(cow.last_calving),
          birthDate: cow && cow.birth_date && this.$moment(cow.birth_date).format(this.$dateFormat),
          lastInsemination: cow && cow.last_insemination && this.$moment(cow.last_insemination).format(this.$dateFormat),
          lastCalved: cow && cow.last_calving && this.$moment(cow.last_calving).format(this.$dateFormat),
          lastSeen: sensor_log && this.getLastSeen(sensor_log.database_entry_time),
          pendingMsg: sensor_log && sensor_log.pending_msg,
          database_entry_time: sensor_log && sensor_log.database_entry_time,
          heat_index: cow && cow.heat_index,
          heat_index_formatted: (cow && cow.heat_index) ? UtilsService.formatPercentage(status.cow.heat_index) : this.$t("general.notAvailable"),
        }
      });
    },
    /**
     * Sorts the data by cow_id in ascending order (smallest in front)
     * @param {array} data data to sort
     */
    sortData(cows) {
      return cows.sort((a, b) => {

        // Get values by keys
        let aKey, bKey;
        aKey = a ? a[this.sortBy.key] : null;
        bKey = b ? b[this.sortBy.key] : null;

        if (['birthDate', 'lastCalved', 'lastInsemination'].includes(this.sortBy.key)) {
          // Convert dates to numerical values
          aKey = this.$moment(aKey || '2000-01-01').format('x');
          bKey = this.$moment(bKey || '2000-01-01').format('x');
        }

        if(this.sortBy.key == "lastSeen"){
          aKey = this.$moment(b.database_entry_time || '2000-01-01').format('x')
          bKey = this.$moment(a.database_entry_time || '2000-01-01').format('x')
        }

        //Compare numerical values
        let diff = aKey - bKey;

        // Compare strings
        if (['barn', 'eartag'].includes(this.sortBy.key)){
          // Check for direction(string)
          if (this.sortBy.direction)
            diff = (bKey || '').localeCompare(aKey)
          else
            diff = (aKey || '').localeCompare(bKey)
        }
        else
          // Numerical, check for direction
          if (this.sortBy.direction)
            diff *= -1;

        return diff;
      })
    },

    getLastSeen(dateString) {
      if (!dateString) {
        return this.$t("general.never").toUpperCase();
      }

      const dateTime = this.$moment(dateString);

      const now = this.$moment();

      const diff = now - dateTime;
      const diffInDays = Math.ceil(diff / 1000 / 60 / 60 / 24);

      if (diffInDays === 1) {
        const diffInHours = Math.ceil(diff / 1000 / 60 / 60);
        if (diffInHours === 1) {
          const diffInMinutes = Math.ceil(diff / 1000 / 60);
          return `${diffInMinutes} minutes ago`;
        }
        return `${diffInHours} hours ago`;
      }

      return `${diffInDays} days ago`;
    },
  }
};



// WEBPACK FOOTER //
// ./src/components/_dashboard/states/table/states-table.js